import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import PageContainer from "../../../components/PageContainer";
import { getClientes } from "../../../services/modules/Clientes";
import { getServicios } from "../../../services/modules/ServiciosCatalogo";
import { registrarServicio } from "../../../services/modules/ServiciosVentas";

const currencyGTQ = new Intl.NumberFormat("es-GT", {
  style: "currency",
  currency: "GTQ",
  minimumFractionDigits: 2,
});

const round2 = (value) => +Number(value || 0).toFixed(2);
const normalizeCategoria = (value) => String(value || "").trim();
const isCategoriaFree = (categoria) => normalizeCategoria(categoria).toUpperCase().startsWith("FREE");
const calcCostoAjustadoUnit = ({ costo, porcentaje, categoria }) => {
  const base = Number(costo || 0);
  const pct = Number(porcentaje || 0);
  if (!isCategoriaFree(categoria)) return round2(base);
  return round2(base * (1 + pct / 100));
};

const toArray = (x) =>
  Array.isArray(x) ? x : Array.isArray(x?.data) ? x.data : Array.isArray(x?.items) ? x.items : [];

const obtenerFechaLocal = () => {
  const ahora = new Date();
  const offsetMs = ahora.getTimezoneOffset() * 60000;
  const localTime = new Date(ahora.getTime() - offsetMs);
  return localTime.toISOString().slice(0, 16);
};

const ServiciosVentas = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [clientes, setClientes] = useState([]);
  const [clienteInput, setClienteInput] = useState("");
  const [idCliente, setIdCliente] = useState("");

  const [servicios, setServicios] = useState([]);
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);
  const [servicioQuery, setServicioQuery] = useState("");

  const [cantidad, setCantidad] = useState("1");
  const cantidadNum = parseInt(cantidad, 10);
  const isCantidadValida = Number.isFinite(cantidadNum) && cantidadNum > 0;

  const [fecha, setFecha] = useState(obtenerFechaLocal());
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");

  const [detalle, setDetalle] = useState([]);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getClientes();
        setClientes(toArray(data));
      } catch {
        setClientes([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await getServicios({ q: servicioQuery, activos: "1" });
        setServicios(Array.isArray(data) ? data : []);
      } catch {
        setServicios([]);
      }
    })();
  }, [servicioQuery]);

  const agregar = () => {
    if (!servicioSeleccionado) return;
    if (!isCantidadValida) return alert("Cantidad inválida");

    const precio = Number(servicioSeleccionado.precio_base || 0);
    const costo = Number(servicioSeleccionado.costo_externo_base || 0);
    const categoria = servicioSeleccionado.categoria;
    const porcentaje = Number(servicioSeleccionado.porcentaje_recargo_costo_ext || 0);
    const porcentajeAplicado = isCategoriaFree(categoria) ? porcentaje : 0;
    const costoAjustado = calcCostoAjustadoUnit({
      costo,
      porcentaje: porcentajeAplicado,
      categoria,
    });
    const qty = cantidadNum;

    setDetalle((prev) => [
      ...prev,
      {
        id_servicio: servicioSeleccionado.id_servicio,
        nombre: servicioSeleccionado.nombre,
        categoria,
        porcentaje_recargo_costo_ext: porcentajeAplicado,
        cantidad: qty,
        precio_unitario: precio,
        costo_externo_unitario: costo,
        costo_externo_unitario_ajustado: costoAjustado,
        total_costo_externo_ajustado: round2(qty * costoAjustado),
        total: round2(qty * precio),
        ganancia: round2(qty * (precio - costoAjustado)),
      },
    ]);

    setServicioSeleccionado(null);
    setServicioQuery("");
    setCantidad("1");
  };

  const eliminar = (index) => {
    setDetalle((prev) => prev.filter((_, i) => i !== index));
  };

  const totals = useMemo(() => {
    const total = detalle.reduce((acc, it) => acc + Number(it.total || 0), 0);
    const costo = detalle.reduce(
      (acc, it) => acc + Number(it.cantidad || 0) * Number(it.costo_externo_unitario || 0),
      0
    );
    const costoAjustado = detalle.reduce(
      (acc, it) => acc + Number(it.cantidad || 0) * Number(it.costo_externo_unitario_ajustado || 0),
      0
    );
    const ganancia = round2(total - costoAjustado);
    return {
      total: round2(total),
      costo: round2(costo),
      costo_ajustado: round2(costoAjustado),
      ganancia,
    };
  }, [detalle]);

  const resumenPorCategoria = useMemo(() => {
    const map = new Map();
    for (const it of detalle) {
      const key = normalizeCategoria(it.categoria) || "SIN CATEGORIA";
      if (!map.has(key)) {
        map.set(key, { categoria: key, total: 0, costo: 0, costo_ajustado: 0, ganancia: 0 });
      }
      const row = map.get(key);
      row.total += Number(it.total || 0);
      row.costo += Number(it.cantidad || 0) * Number(it.costo_externo_unitario || 0);
      row.costo_ajustado += Number(it.cantidad || 0) * Number(it.costo_externo_unitario_ajustado || 0);
      row.ganancia += Number(it.ganancia || 0);
    }

    return Array.from(map.values())
      .map((r) => ({
        ...r,
        total: round2(r.total),
        costo: round2(r.costo),
        costo_ajustado: round2(r.costo_ajustado),
        ganancia: round2(r.ganancia),
      }))
      .sort((a, b) => a.categoria.localeCompare(b.categoria));
  }, [detalle]);

  const guardar = async () => {
    if (detalle.length === 0) return alert("Agrega al menos un servicio");
    setProcessing(true);
    setError(null);
    try {
      await registrarServicio({
        fecha,
        id_cliente: idCliente || null,
        metodo_pago: metodoPago,
        items: detalle.map((it) => ({
          id_servicio: it.id_servicio,
          cantidad: it.cantidad,
          descripcion: it.nombre,
          precio_unitario: it.precio_unitario,
          costo_externo_unitario: it.costo_externo_unitario,
        })),
      });

      alert("Servicio registrado y enviado a caja");
      setDetalle([]);
      setServicioSeleccionado(null);
      setServicioQuery("");
      setCantidad("1");
      setIdCliente("");
      setMetodoPago("EFECTIVO");
      setFecha(obtenerFechaLocal());
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al registrar servicio");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PageContainer title="Servicios (cobros)">
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={clientes}
              value={clientes.find((cli) => String(cli.id_cliente) === String(idCliente)) || null}
              onChange={(_, newValue) => setIdCliente(newValue?.id_cliente || "")}
              inputValue={clienteInput}
              onInputChange={(_, value) => setClienteInput(value)}
              isOptionEqualToValue={(option, value) => String(option.id_cliente) === String(value?.id_cliente)}
              getOptionLabel={(option) =>
                option ? `${option.nombre || "Sin nombre"} · NIT ${option.nit || "CF"}` : ""
              }
              renderInput={(params) => <TextField {...params} label="Cliente (opcional)" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Fecha"
              type="datetime-local"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              label="Método"
              select
              value={metodoPago}
              onChange={(e) => setMetodoPago(e.target.value)}
              fullWidth
            >
              <option value="EFECTIVO">EFECTIVO</option>
              <option value="TARJETA">TARJETA</option>
              <option value="TRANSFERENCIA">TRANSFERENCIA</option>
            </TextField>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Autocomplete
              options={servicios}
              value={servicioSeleccionado}
              onChange={(_, value) => setServicioSeleccionado(value)}
              inputValue={servicioQuery}
              onInputChange={(_, value) => setServicioQuery(value)}
              getOptionLabel={(o) => {
                if (!o) return "";
                const cod = o.codigo ? `[${o.codigo}] ` : "";
                const cat = o.categoria ? ` - ${o.categoria}` : "";
                return `${cod}${o.nombre}${cat} · ${currencyGTQ.format(Number(o.precio_base || 0))}`;
              }}
              isOptionEqualToValue={(o, v) => o?.id_servicio === v?.id_servicio}
              renderInput={(params) => <TextField {...params} label="Servicio" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Cantidad"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              type="number"
              inputProps={{ min: 1, step: 1 }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={agregar}
              disabled={!servicioSeleccionado || !isCantidadValida}
              fullWidth
            >
              Agregar
            </Button>
          </Grid>
        </Grid>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detalle
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cant.</TableCell>
                <TableCell>Servicio</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Costo ext.</TableCell>
                <TableCell align="right">Recargo %</TableCell>
                <TableCell align="right">Costo +%</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Ganancia</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {detalle.map((it, idx) => (
                <TableRow key={`${it.id_servicio}-${idx}`}>
                  <TableCell>{it.cantidad}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {it.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {it.categoria || "SIN CATEGORIA"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={it.precio_unitario}
                      onChange={(e) => {
                        const price = Number(e.target.value || 0);
                        setDetalle((prev) => {
                          const copy = [...prev];
                          const row = { ...copy[idx] };
                          row.precio_unitario = price;
                          row.total = round2(row.cantidad * row.precio_unitario);
                          row.ganancia = round2(
                            row.total - row.cantidad * Number(row.costo_externo_unitario_ajustado || 0)
                          );
                          copy[idx] = row;
                          return copy;
                        });
                      }}
                      inputProps={{ step: "0.01", min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      value={it.costo_externo_unitario}
                      onChange={(e) => {
                        const cost = Number(e.target.value || 0);
                        setDetalle((prev) => {
                          const copy = [...prev];
                          const row = { ...copy[idx] };
                          row.costo_externo_unitario = cost;
                          row.costo_externo_unitario_ajustado = calcCostoAjustadoUnit({
                            costo: cost,
                            porcentaje: row.porcentaje_recargo_costo_ext,
                            categoria: row.categoria,
                          });
                          row.total_costo_externo_ajustado = round2(
                            row.cantidad * Number(row.costo_externo_unitario_ajustado || 0)
                          );
                          row.total = round2(row.cantidad * row.precio_unitario);
                          row.ganancia = round2(
                            row.total - row.cantidad * Number(row.costo_externo_unitario_ajustado || 0)
                          );
                          copy[idx] = row;
                          return copy;
                        });
                      }}
                      inputProps={{ step: "0.01", min: 0 }}
                    />
                  </TableCell>
                  <TableCell align="right">{Number(it.porcentaje_recargo_costo_ext || 0)}%</TableCell>
                  <TableCell align="right">{currencyGTQ.format(it.total_costo_externo_ajustado || 0)}</TableCell>
                  <TableCell align="right">{currencyGTQ.format(it.total)}</TableCell>
                  <TableCell align="right">{currencyGTQ.format(it.ganancia)}</TableCell>
                  <TableCell align="right">
                    <IconButton color="error" onClick={() => eliminar(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {detalle.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Sin items
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="right">
                    <strong>Totales:</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{currencyGTQ.format(totals.total)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{currencyGTQ.format(totals.ganancia)}</strong>
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>

          {detalle.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Subtotales por categoria
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Costo ext.</TableCell>
                    <TableCell align="right">Costo +%</TableCell>
                    <TableCell align="right">Ganancia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumenPorCategoria.map((r) => (
                    <TableRow key={r.categoria}>
                      <TableCell>{r.categoria}</TableCell>
                      <TableCell align="right">{currencyGTQ.format(r.total)}</TableCell>
                      <TableCell align="right">{currencyGTQ.format(r.costo)}</TableCell>
                      <TableCell align="right">{currencyGTQ.format(r.costo_ajustado)}</TableCell>
                      <TableCell align="right">{currencyGTQ.format(r.ganancia)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box mt={2}>
            <Stack direction={isXs ? "column" : "row"} spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Costo externo total: <strong>{currencyGTQ.format(totals.costo)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Costo +% total: <strong>{currencyGTQ.format(totals.costo_ajustado)}</strong>
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" color="success" onClick={guardar} disabled={processing}>
                Guardar cobro
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Stack>
    </PageContainer>
  );
};

export default ServiciosVentas;
